import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import "./AnalyticsV2.css";

const ORIGIN_KEY =
  (typeof process !== "undefined" && process.env && (process.env.REACT_APP_ORIGIN_KEY || process.env.NEXT_PUBLIC_ORIGIN_KEY)) || "";

const AIRPORTS = [
  { value: "LHR", label: "Heathrow" },
  { value: "EMA", label: "East Midlands" },
];

const PERIODS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "7 days" },
  { value: "last_30_days", label: "30 days" },
];

const COMPARE_BASES = [
  { value: "today", label: "Today vs yesterday" },
  { value: "last_7_days", label: "7d vs previous" },
  { value: "last_30_days", label: "30d vs previous" },
];

const apiRequest = async (url) => {
  const headers = {};
  if (ORIGIN_KEY) headers["X-Origin-Key"] = ORIGIN_KEY;
  const res = await api.get(url, { headers });
  return res.data;
};

const nf = new Intl.NumberFormat("en-GB");

function fmtNum(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return nf.format(n);
}

function fmtPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0%";
  return `${n.toFixed(n % 1 ? 1 : 0)}%`;
}

function fmtDelta(value, suffix = "") {
  const n = Number(value) || 0;
  if (n > 0) return `+${fmtNum(n)}${suffix}`;
  return `${fmtNum(n)}${suffix}`;
}

function compareCell(metric, suffix = "") {
  if (!metric || Number(metric.previous || 0) === 0) return "Current";
  if (Number(metric.current || 0) === 0) return "No current";
  return fmtDelta(metric.delta, suffix);
}

function dateHourLabel(value) {
  if (!value) return "N/A";
  if (typeof value === "string") {
    const match = value.match(/\b(\d{2}):\d{2}/);
    if (match) return `${match[1]}:00`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "N/A";
  return `${String(d.getHours()).padStart(2, "0")}:00`;
}

function statusClass(level) {
  const v = String(level || "unknown").toLowerCase();
  if (v === "green") return "analytics-status ok";
  if (v === "light_green") return "analytics-status light";
  if (v === "amber") return "analytics-status warn";
  if (v === "red") return "analytics-status bad";
  return "analytics-status na";
}

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function metricTrend(delta, higherIsBad = true) {
  const n = Number(delta) || 0;
  if (n === 0) return "flat";
  if (higherIsBad) return n > 0 ? "worse" : "better";
  return n > 0 ? "up" : "down";
}

function AnalyticsV2() {
  const [airport, setAirport] = useState("LHR");
  const [period, setPeriod] = useState("today");
  const [basis, setBasis] = useState("last_7_days");
  const [overview, setOverview] = useState(null);
  const [facts, setFacts] = useState(null);
  const [compare, setCompare] = useState(null);
  const [cacheMeta, setCacheMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const qs = `airport=${encodeURIComponent(airport)}`;
        const dashboard = await apiRequest(
          `/api/analytics/v2/dashboard?${qs}&period=${encodeURIComponent(period)}&basis=${encodeURIComponent(basis)}`
        );
        if (cancelled) return;
        setOverview(dashboard?.reports?.overview || null);
        setFacts(dashboard?.reports?.facts || null);
        setCompare(dashboard?.reports?.compare || null);
        setCacheMeta(dashboard || null);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || err?.message || "Failed to load analytics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [airport, period, basis]);

  const kpis = overview?.kpis || {};
  const compareDeltas = period === basis ? compare?.summary?.deltas || {} : {};
  const storyLines = useMemo(() => {
    return [
      ...safeList(overview?.highlights?.social_lines),
      ...safeList(facts?.story_lines),
      ...safeList(compare?.story_lines),
      ...safeList(cacheMeta?.reports?.road?.overview?.highlights?.story_lines),
      ...safeList(cacheMeta?.reports?.road?.facts?.story_lines),
      ...safeList(cacheMeta?.reports?.road?.compare?.story_lines),
      ...safeList(cacheMeta?.reports?.weather?.overview?.highlights?.story_lines),
      ...safeList(cacheMeta?.reports?.weather?.facts?.story_lines),
      ...safeList(cacheMeta?.reports?.weather?.compare?.story_lines),
      ...safeList(cacheMeta?.reports?.tracking?.overview?.highlights?.story_lines),
      ...safeList(cacheMeta?.reports?.tracking?.facts?.story_lines),
      ...safeList(cacheMeta?.reports?.tracking?.compare?.story_lines),
      ...safeList(cacheMeta?.reports?.rail?.overview?.highlights?.story_lines),
      ...safeList(cacheMeta?.reports?.rail?.facts?.story_lines),
      ...safeList(cacheMeta?.reports?.rail?.compare?.story_lines),
    ].slice(0, 16);
  }, [overview, facts, compare, cacheMeta]);
  const queueReports = cacheMeta?.reports?.terminal_queues || {};
  const roadReports = cacheMeta?.reports?.road || {};
  const weatherReports = cacheMeta?.reports?.weather || {};
  const trackingReports = cacheMeta?.reports?.tracking || {};
  const railReports = cacheMeta?.reports?.rail || {};
  const weatherAlertRows = useMemo(() => {
    const alerts = safeList(weatherReports?.overview?.alerts).filter((row) => {
      const open = String(row.status || "").toLowerCase() === "open";
      const severity = String(row.current_severity || row.status_label || "green").toLowerCase();
      return open && severity !== "green";
    });
    return [...alerts, ...safeList(weatherReports?.facts?.facts?.unusual_observations)].slice(0, 10);
  }, [weatherReports]);
  const roadMovementRows = useMemo(() => {
    return safeList(roadReports?.compare?.rankings?.corridors)
      .filter((row) => Number(row.current?.samples || 0) > 0 || Number(row.deltas?.issue_samples?.delta || 0) !== 0 || Number(row.deltas?.avg_delay_mins?.delta || 0) !== 0)
      .sort((a, b) => {
        const aScore = Math.abs(Number(a.deltas?.issue_samples?.delta || 0)) + Math.abs(Number(a.deltas?.avg_delay_mins?.delta || 0));
        const bScore = Math.abs(Number(b.deltas?.issue_samples?.delta || 0)) + Math.abs(Number(b.deltas?.avg_delay_mins?.delta || 0));
        return bScore - aScore;
      });
  }, [roadReports]);
  const railMovementRows = useMemo(() => {
    return safeList(railReports?.compare?.rankings?.stations)
      .filter((row) => Number(row.current?.service_count || 0) > 0 || Number(row.deltas?.delayed_count?.delta || 0) !== 0 || Number(row.deltas?.cancelled_count?.delta || 0) !== 0)
      .sort((a, b) => {
        const aScore = Math.abs(Number(a.deltas?.delayed_count?.delta || 0)) + Math.abs(Number(a.deltas?.cancelled_count?.delta || 0)) * 3;
        const bScore = Math.abs(Number(b.deltas?.delayed_count?.delta || 0)) + Math.abs(Number(b.deltas?.cancelled_count?.delta || 0)) * 3;
        return bScore - aScore;
      });
  }, [railReports]);
  const railStoryLines = useMemo(() => {
    return [
      ...safeList(railReports?.overview?.highlights?.story_lines),
      ...safeList(railReports?.facts?.story_lines),
      ...safeList(railReports?.compare?.story_lines),
    ].slice(0, 10);
  }, [railReports]);

  return (
    <main className="analytics-page">
      <section className="analytics-topbar">
        <div>
          <p className="analytics-kicker">Analytics v2</p>
          <h1>Airport Flight Reporting</h1>
        </div>
        <div className="analytics-controls" aria-label="Analytics filters">
          <SegmentedControl value={airport} onChange={setAirport} options={AIRPORTS} label="Airport" />
          <SelectControl value={period} onChange={setPeriod} options={PERIODS} label="Facts period" />
          <SelectControl value={basis} onChange={setBasis} options={COMPARE_BASES} label="Compare" />
        </div>
      </section>

      {error ? <div className="analytics-error">{error}</div> : null}
      {loading ? <div className="analytics-loading">Loading analytics...</div> : null}

      {!loading && !error ? (
        <>
          <section className="analytics-grid analytics-grid-kpis">
            <MetricCard label="Flights" value={fmtNum(kpis.total_flights)} delta={compareDeltas.total_flights?.delta} higherIsBad={false} compareHidden={period !== basis} />
            <MetricCard label="Delayed 15m+" value={fmtNum(kpis.delayed_15)} sub={fmtPct(kpis.delayed_15_rate_pct)} delta={compareDeltas.delayed_15?.delta} compareHidden={period !== basis} />
            <MetricCard label="Cancelled" value={fmtNum(kpis.cancelled)} sub={fmtPct(kpis.cancellation_rate_pct)} delta={compareDeltas.cancelled?.delta} compareHidden={period !== basis} />
            <MetricCard label="On Time" value={fmtPct(kpis.on_time_rate_pct)} delta={compareDeltas.on_time_rate_pct?.delta} suffix=" pts" higherIsBad={false} compareHidden={period !== basis} />
            <MetricCard label="Avg Delay" value={`${fmtNum(kpis.avg_delay_mins)}m`} delta={compareDeltas.avg_delay_mins?.delta} suffix="m" compareHidden={period !== basis} />
          </section>

          <section className="analytics-layout">
            <div className="analytics-main">
              <section className="analytics-panel">
                <PanelHeader title="Delay And Cancellation Trend" meta={compare?.labels ? `${compare.labels.current} vs ${compare.labels.previous}` : null} />
                <CompareChart rows={safeList(compare?.series)} />
              </section>

              <section className="analytics-two-col">
                <RankingTable
                  title="Airline Movement"
                  rows={safeList(compare?.rankings?.airlines)}
                  columns={[
                    ["Airline", (r) => r.airline_name || r.airline_code || "Unknown"],
                    ["Flights", (r) => fmtDelta(r.deltas?.total_flights?.delta)],
                    ["Delay", (r) => fmtDelta(r.deltas?.delayed_15?.delta)],
                    ["Cancel", (r) => fmtDelta(r.deltas?.cancelled?.delta)],
                  ]}
                />
                <RankingTable
                  title="Terminal Movement"
                  rows={safeList(compare?.rankings?.terminals)}
                  columns={[
                    ["Terminal", (r) => (r.terminal === "Unknown" ? "Unknown" : `T${r.terminal}`)],
                    ["Flights", (r) => fmtDelta(r.deltas?.total_flights?.delta)],
                    ["Delay", (r) => fmtDelta(r.deltas?.delayed_15?.delta)],
                    ["Cancel", (r) => fmtDelta(r.deltas?.cancelled?.delta)],
                  ]}
                />
              </section>

              <section className="analytics-two-col">
                <RankingTable
                  title="Busiest Airlines"
                  rows={safeList(facts?.facts?.busiest_airlines)}
                  columns={[
                    ["Airline", (r) => r.airline_name || "Unknown"],
                    ["Flights", (r) => fmtNum(r.total_flights)],
                    ["On time", (r) => fmtPct(r.on_time_rate_pct)],
                    ["Delay", (r) => fmtPct(r.delayed_15_rate_pct)],
                  ]}
                />
                <RankingTable
                  title="Most Punctual Airlines"
                  rows={safeList(facts?.facts?.most_punctual_airlines)}
                  columns={[
                    ["Airline", (r) => r.airline_name || "Unknown"],
                    ["Flights", (r) => fmtNum(r.total_flights)],
                    ["On time", (r) => fmtPct(r.on_time_rate_pct)],
                    ["Avg", (r) => `${fmtNum(r.avg_delay_mins)}m`],
                  ]}
                />
              </section>

              <section className="analytics-two-col">
                <RankingTable
                  title="Busiest Routes"
                  rows={safeList(facts?.facts?.busiest_route_airports)}
                  columns={[
                    ["Airport", (r) => [r.route_airport_code, r.route_city].filter(Boolean).join(" / ") || "Unknown"],
                    ["Flights", (r) => fmtNum(r.total_flights)],
                    ["Arr", (r) => fmtNum(r.arrivals)],
                    ["Dep", (r) => fmtNum(r.departures)],
                  ]}
                />
                <RankingTable
                  title="Longest Flights"
                  rows={safeList(facts?.facts?.longest_flights)}
                  empty="Duration is not available for this airport."
                  columns={[
                    ["Flight", (r) => r.flight_identifier || r.flight_number || "Unknown"],
                    ["Route", (r) => [r.origin, r.destination].filter(Boolean).join("-")],
                    ["Airline", (r) => r.airline_name || "Unknown"],
                    ["Hours", (r) => (r.scheduled_duration_hours ? `${r.scheduled_duration_hours}h` : "N/A")],
                  ]}
                />
              </section>

              <section className="analytics-two-col">
                <RankingTable
                  title="Busiest Check-In Zones"
                  rows={safeList(facts?.facts?.busiest_checkin_zones)}
                  empty="Check-in zones are not available for this airport."
                  columns={[
                    ["Zone", (r) => `${r.terminal === "Unknown" ? "Unknown" : `T${r.terminal}`} ${r.checkin_zone || ""}`.trim()],
                    ["Flights", (r) => fmtNum(r.total_flights)],
                    ["Airlines", (r) => fmtNum(r.airline_count)],
                    ["Main users", (r) => safeList(r.airlines).slice(0, 2).join(", ")],
                  ]}
                />
                <RankingTable
                  title="Gates And Belts"
                  rows={[...safeList(facts?.facts?.busiest_gates).slice(0, 5), ...safeList(facts?.facts?.busiest_baggage_belts).slice(0, 5)]}
                  columns={[
                    ["Asset", (r) => (r.gate_number ? `Gate ${r.gate_number}` : `Belt ${r.baggage_carousel_number}`)],
                    ["Terminal", (r) => (r.terminal === "Unknown" ? "Unknown" : `T${r.terminal}`)],
                    ["Flights", (r) => fmtNum(r.total_flights)],
                    ["Airlines", (r) => fmtNum(r.airline_count)],
                  ]}
                />
              </section>

              {airport === "LHR" ? (
                <section className="analytics-two-col">
                  <RankingTable
                    title="Terminal Queues Now"
                    rows={safeList(queueReports?.overview?.latest)}
                    limit={20}
                    empty="No cached queue snapshot yet."
                    columns={[
                      ["Queue", (r) => `T${r.terminal} ${r.queue_label}`],
                      ["Wait", (r) => (r.max_wait_minutes == null ? "N/A" : `${fmtNum(r.max_wait_minutes)}m`)],
                      ["Trend", (r) => (r.delta_wait_minutes == null ? "Flat" : fmtDelta(r.delta_wait_minutes, "m"))],
                      ["Status", (r) => r.wait_message || r.status_label || "N/A"],
                    ]}
                  />
                  <RankingTable
                    title="Queue Peaks"
                    rows={safeList(queueReports?.facts?.busiest_queues)}
                    empty="No cached queue facts yet."
                    columns={[
                      ["Queue", (r) => `T${r.terminal} ${r.queue_label}`],
                      ["Avg", (r) => `${fmtNum(r.avg_wait_minutes)}m`],
                      ["Peak", (r) => `${fmtNum(r.max_wait_minutes)}m`],
                      ["Busy hour", (r) => (r.busiest_hour_local == null ? "N/A" : `${String(r.busiest_hour_local).padStart(2, "0")}:00`)],
                    ]}
                  />
                </section>
              ) : null}

              {airport === "LHR" ? (
                <section className="analytics-two-col">
                  <section className="analytics-panel">
                    <PanelHeader
                      title="Queue Movement"
                      meta={queueReports?.compare?.labels ? `${queueReports.compare.labels.current} vs ${queueReports.compare.labels.previous}` : "Cached"}
                    />
                    <QueueMovement rows={safeList(queueReports?.compare?.queues)} />
                  </section>
                  <RankingTable
                    title="Busiest Queue Hours"
                    rows={safeList(queueReports?.facts?.busiest_hours)}
                    empty="No cached queue hour facts yet."
                    columns={[
                      ["Hour", (r) => r.hour_label || "N/A"],
                      ["Queue", (r) => `T${r.terminal} ${r.queue_label}`],
                      ["15m+", (r) => fmtNum(r.samples_15_plus)],
                      ["Peak", (r) => `${fmtNum(r.max_wait_minutes)}m`],
                    ]}
                  />
                </section>
              ) : null}

              {airport === "LHR" ? (
                <RankingTable
                  title="Queue And Flight Waves"
                  rows={safeList(queueReports?.facts?.flight_correlations)}
                  empty="No cached queue and flight correlation yet."
                  columns={[
                    ["Queue", (r) => `T${r.terminal} ${r.queue_label}`],
                    ["Hour", (r) => r.hour_label || "N/A"],
                    ["Peak", (r) => `${fmtNum(r.max_wait_minutes)}m`],
                    ["Flights", (r) => `${fmtNum(r.relevant_flights)} ${r.linked_flight_direction || ""}`.trim()],
                    ["Intl", (r) => fmtNum(r.relevant_international_flights)],
                    ["Long haul", (r) => fmtNum(r.relevant_long_haul_flights)],
                    ["Top routes", (r) => safeList(r.linked_routes).map((route) => `${route.route_airport_code} ${route.flight_count}`).join(", ") || "N/A"],
                  ]}
                />
              ) : null}

              <section className="analytics-two-col">
                <RankingTable
                  title="Road Corridors Now"
                  rows={safeList(roadReports?.overview?.rankings?.corridors)}
                  empty="No cached road overview yet."
                  columns={[
                    ["Corridor", (r) => r.display_name || r.corridor_key],
                    ["Status", (r) => String(r.status_label || "unknown").toUpperCase()],
                    ["Delay", (r) => `${fmtNum(r.delay_mins)}m`],
                    ["Live", (r) => `${fmtNum(r.live_duration_mins)}m`],
                  ]}
                />
                <RankingTable
                  title="Affected Road Segments"
                  rows={safeList(roadReports?.overview?.rankings?.segments)}
                  empty="No cached road segment overview yet."
                  columns={[
                    ["Segment", (r) => r.display_label || r.segment_key],
                    ["Corridor", (r) => r.corridor_name || r.corridor_key],
                    ["Delay", (r) => `${fmtNum(r.delay_mins)}m`],
                    ["Status", (r) => String(r.status_label || "unknown").toUpperCase()],
                  ]}
                />
              </section>

              <section className="analytics-two-col">
                <RankingTable
                  title="Road Movement"
                  rows={roadMovementRows}
                  empty="No cached road comparison yet."
                  columns={[
                    ["Corridor", (r) => r.display_name || r.corridor_key],
                    ["Samples", (r) => fmtNum(r.current?.samples)],
                    ["Issues", (r) => compareCell(r.deltas?.issue_samples)],
                    ["Avg delay", (r) => compareCell(r.deltas?.avg_delay_mins, "m")],
                  ]}
                />
                <RankingTable
                  title="Road Issue Hours"
                  rows={safeList(roadReports?.facts?.facts?.busiest_hours)}
                  empty="No cached road hour facts yet."
                  columns={[
                    ["Hour", (r) => String(r.hour_local || "").slice(11, 16) || "N/A"],
                    ["Issues", (r) => fmtNum(r.issue_count)],
                    ["Avg", (r) => `${fmtNum(r.avg_delay_mins)}m`],
                    ["Worst", (r) => `${fmtNum(r.worst_delay_mins)}m`],
                  ]}
                />
              </section>

              <section className="analytics-two-col">
                <RankingTable
                  title="Road Corridor Facts"
                  rows={safeList(roadReports?.facts?.facts?.corridors)}
                  empty="No cached road corridor facts yet."
                  columns={[
                    ["Corridor", (r) => r.display_name || r.corridor_key],
                    ["Samples", (r) => fmtNum(r.sample_count)],
                    ["Issues", (r) => fmtNum(r.issue_count)],
                    ["Peak", (r) => `${fmtNum(r.worst_delay_mins)}m`],
                  ]}
                />
                <RankingTable
                  title="Road Alerts"
                  rows={safeList(roadReports?.overview?.rankings?.alerts)}
                  empty="No open cached road alerts."
                  columns={[
                    ["Source", (r) => r.alert_source === "national_highways" ? "NH" : "Traffic"],
                    ["Segment", (r) => r.display_name || r.segment_key],
                    ["Severity", (r) => String(r.current_severity || "unknown").toUpperCase()],
                    ["Delay", (r) => `${fmtNum(r.metric_value_mins)}m`],
                  ]}
                />
              </section>

              <section className="analytics-two-col">
                <RankingTable
                  title="Weather Now"
                  rows={weatherReports?.overview?.latest ? [weatherReports.overview.latest] : []}
                  empty="No cached weather overview yet."
                  columns={[
                    ["Airport", () => airport],
                    ["Condition", (r) => r.weather_text || "Unknown"],
                    ["Temp", (r) => `${fmtNum(r.temp_c)}C`],
                    ["Wind", (r) => `${fmtNum(r.wind_mps)}m/s`],
                    ["Rain", (r) => `${fmtNum(r.precip_mm)}mm`],
                    ["Status", (r) => String(r.status_label || "unknown").toUpperCase()],
                  ]}
                />
                <RankingTable
                  title="Weather Movement"
                  rows={weatherReports?.compare?.summary ? [weatherReports.compare.summary] : []}
                  empty="No cached weather comparison yet."
                  columns={[
                    ["Metric", () => weatherReports?.compare?.labels ? `${weatherReports.compare.labels.current} vs ${weatherReports.compare.labels.previous}` : "Compare"],
                    ["Avg temp", (r) => fmtDelta(r.deltas?.avg_temp_c?.delta, "C")],
                    ["Peak wind", (r) => fmtDelta(r.deltas?.peak_wind_mps?.delta, "m/s")],
                    ["Rain", (r) => fmtDelta(r.deltas?.total_precip_mm?.delta, "mm")],
                    ["Unusual", (r) => fmtDelta(r.deltas?.unusual_samples?.delta)],
                  ]}
                />
              </section>

              <section className="analytics-two-col">
                <RankingTable
                  title="Weather Trend Hours"
                  rows={safeList(weatherReports?.facts?.facts?.hours)}
                  empty="No cached weather trend facts yet."
                  columns={[
                    ["Hour", (r) => r.hour_label || dateHourLabel(r.hour_local)],
                    ["Avg temp", (r) => `${fmtNum(r.avg_temp_c)}C`],
                    ["Peak wind", (r) => `${fmtNum(r.peak_wind_mps)}m/s`],
                    ["Rain", (r) => `${fmtNum(r.total_precip_mm)}mm`],
                    ["Unusual", (r) => fmtNum(r.unusual_samples)],
                  ]}
                />
                <RankingTable
                  title="Weather Alerts And Unusual"
                  rows={weatherAlertRows}
                  empty="No open or unusual cached weather items."
                  columns={[
                    ["Type", (r) => r.target_key ? "Alert" : "Observation"],
                    ["Status", (r) => String(r.current_severity || r.status_label || "unknown").toUpperCase()],
                    ["Reason", (r) => r.reason_text || r.weather_text || "Weather"],
                    ["Wind", (r) => (r.wind_mps == null ? "N/A" : `${fmtNum(r.wind_mps)}m/s`)],
                    ["Rain", (r) => (r.precip_mm == null ? "N/A" : `${fmtNum(r.precip_mm)}mm`)],
                  ]}
                />
              </section>

              {airport === "LHR" ? (
                <section className="analytics-two-col">
                  <RankingTable
                    title="Flight Tracking ML Snapshot"
                    rows={!trackingReports?.overview?.unavailable && trackingReports?.overview?.summary ? [trackingReports.overview.summary] : []}
                    empty="No cached tracking analytics yet."
                    columns={[
                      ["Snapshot", (r) => r.snapshot_date || "N/A"],
                      ["Fresh tracked", (r) => fmtNum(r.fresh_flights)],
                      ["Delayed signal", (r) => fmtNum(r.delayed_signal_flights)],
                      ["Consistent", (r) => fmtPct(r.consistent_rate_pct)],
                      ["Coverage", (r) => fmtNum(r.avg_coverage_score)],
                      ["Bench rows", (r) => fmtNum(r.benchmark_rows)],
                    ]}
                  />
                  <RankingTable
                    title="Tracking Benchmark Movement"
                    rows={!trackingReports?.compare?.unavailable && trackingReports?.compare?.summary ? [trackingReports.compare.summary] : []}
                    empty="No cached tracking comparison yet."
                    columns={[
                      ["Metric", () => trackingReports?.compare?.labels ? `${trackingReports.compare.labels.current} vs ${trackingReports.compare.labels.previous}` : "Compare"],
                      ["Rows", (r) => fmtDelta(r.deltas?.benchmark_rows?.delta)],
                      ["Labels", (r) => fmtDelta(r.deltas?.confirmed_labels?.delta)],
                      ["MAE", (r) => fmtDelta(r.deltas?.mae_minutes?.delta, "m")],
                      ["Median", (r) => fmtDelta(r.deltas?.median_abs_error_minutes?.delta, "m")],
                    ]}
                  />
                </section>
              ) : null}

              {airport === "LHR" ? (
                <section className="analytics-two-col">
                  <RankingTable
                    title="Recently Tracked Flights"
                    rows={safeList(trackingReports?.overview?.rankings?.recent_tracked_flights)}
                    empty="No cached recent tracked flights."
                    columns={[
                      ["Flight", (r) => r.flight_identifier || "Unknown"],
                      ["Route", (r) => r.route || "N/A"],
                      ["Phase", (r) => r.flight_phase || "N/A"],
                      ["Signal", (r) => r.timing_signal || "N/A"],
                      ["Coverage", (r) => `${r.coverage_quality || "N/A"} ${fmtNum(r.coverage_score)}`],
                    ]}
                  />
                  <RankingTable
                    title="Tracking Benchmark Errors"
                    rows={safeList(trackingReports?.facts?.facts?.worst_errors)}
                    empty="No labelled tracking benchmark errors yet."
                    columns={[
                      ["Flight", (r) => r.flight_identifier || "Unknown"],
                      ["Route", (r) => r.route || "N/A"],
                      ["Forecast", (r) => r.forecast_delay_band_minutes == null ? "N/A" : `${fmtNum(r.forecast_delay_band_minutes)}m`],
                      ["Actual", (r) => r.target_delay_minutes == null ? "N/A" : `${fmtNum(r.target_delay_minutes)}m`],
                      ["Abs err", (r) => r.abs_error_minutes == null ? "N/A" : `${fmtNum(r.abs_error_minutes)}m`],
                    ]}
                  />
                </section>
              ) : null}

              <section className="analytics-two-col">
                <section className="analytics-panel">
                  <PanelHeader title="Rail Story Lines" meta={railReports?.overview ? "Cached" : "No cache"} />
                  {railStoryLines.length ? (
                    <div className="analytics-story-list">
                      {railStoryLines.map((line, idx) => (
                        <div className="analytics-story" key={`${line}-${idx}`}>
                          {line}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="analytics-empty">No cached rail story lines yet.</div>
                  )}
                </section>
                <RankingTable
                  title="Rail Status Now"
                  rows={safeList(railReports?.overview?.corridors)}
                  empty="No cached rail overview yet."
                  columns={[
                    ["Corridor", (r) => r.display_name || r.corridor_key],
                    ["Status", (r) => String(r.status_label || "unknown").toUpperCase()],
                    ["Delay", (r) => fmtNum(r.delayed_services_count)],
                    ["Cancel", (r) => fmtNum(r.cancelled_services_count)],
                  ]}
                />
                <RankingTable
                  title="Rail Movement"
                  rows={railMovementRows}
                  empty="No cached rail comparison yet."
                  columns={[
                    ["Station", (r) => r.display_name || r.key],
                    ["Samples", (r) => fmtNum(r.current?.service_count)],
                    ["Delay", (r) => compareCell(r.deltas?.delayed_count)],
                    ["Cancel", (r) => compareCell(r.deltas?.cancelled_count)],
                  ]}
                />
              </section>

              <section className="analytics-two-col">
                <RankingTable
                  title="Rail Station Pressure"
                  rows={safeList(railReports?.facts?.facts?.stations)}
                  empty="No cached rail station facts yet."
                  columns={[
                    ["Station", (r) => r.display_name || r.target_key],
                    ["Services", (r) => fmtNum(r.service_count)],
                    ["Delay", (r) => fmtNum(r.delayed_count)],
                    ["Cancel", (r) => fmtNum(r.cancelled_count)],
                    ["On time", (r) => fmtPct(r.on_time_rate_pct)],
                  ]}
                />
                <RankingTable
                  title="Rail Corridor Performance"
                  rows={safeList(railReports?.facts?.facts?.corridors)}
                  empty="No cached rail corridor facts yet."
                  columns={[
                    ["Corridor", (r) => r.display_name || r.corridor_key],
                    ["Avg delta", (r) => `${fmtDelta(r.avg_delta_mins, "m")}`],
                    ["Worst", (r) => `${fmtNum(r.worst_delta_mins)}m`],
                    ["Score", (r) => fmtNum(r.disruption_score)],
                  ]}
                />
              </section>

              <section className="analytics-two-col">
                <RankingTable
                  title="Rail Operator Performance"
                  rows={safeList(railReports?.facts?.facts?.operators)}
                  empty="No cached rail operator facts yet."
                  columns={[
                    ["Operator", (r) => r.operator_name || "Unknown"],
                    ["Services", (r) => fmtNum(r.service_rows)],
                    ["Delay", (r) => fmtNum(r.delayed_count)],
                    ["Cancel", (r) => fmtNum(r.cancelled_count)],
                    ["Worst", (r) => `${fmtNum(r.worst_delay_mins)}m`],
                  ]}
                />
                <RankingTable
                  title="TfL Journey Health"
                  rows={safeList(railReports?.facts?.facts?.tfl_journeys)}
                  empty="No cached TfL journey facts yet."
                  columns={[
                    ["Target", (r) => r.display_name || r.target_key],
                    ["Avg", (r) => `${fmtNum(r.avg_duration_mins)}m`],
                    ["Slowest", (r) => `${fmtNum(r.slowest_duration_mins)}m`],
                    ["Changes", (r) => fmtNum(r.max_interchanges)],
                  ]}
                />
              </section>

              <RankingTable
                title="Rail Alert And RailData CRS Checks"
                rows={safeList(railReports?.facts?.facts?.raildata_crs_checks)}
                empty="No cached RailData CRS checks yet."
                columns={[
                  ["CRS", (r) => r.crs || "N/A"],
                  ["Station", (r) => r.display_name || r.target_key],
                  ["Alert", (r) => String(r.alert_severity || r.current_severity || "unknown").toUpperCase()],
                  ["Latest board", (r) => String(r.latest_board_status_label || "unknown").toUpperCase()],
                  ["RailData", (r) => String(r.raildata_severity || (Number(r.incident_count || 0) > 0 ? "amber" : "green")).toUpperCase()],
                  ["Incidents", (r) => fmtNum(r.incident_count)],
                  ["Board cancel", (r) => fmtNum(r.latest_board_cancelled_count ?? r.cancelled_services_count)],
                  ["Freshness", (r) => (r.alert_stale_against_board ? "Alert stale" : "Fresh")],
                  ["RailData incident", (r) => safeList(r.raildata_incidents)[0]?.title || "Clear"],
                ]}
              />

              <RankingTable
                title="Airport Rail Service Disruption"
                rows={safeList(railReports?.facts?.facts?.worst_services)}
                empty="No delayed or cancelled airport-relevant rail services."
                columns={[
                  ["Station", (r) => r.target_name || r.target_key],
                  ["Service", (r) => [r.origin_crs, r.destination_crs].filter(Boolean).join("-") || r.service_id || "Unknown"],
                  ["Operator", (r) => r.operator_name || "Unknown"],
                  ["Status", (r) => (r.is_cancelled ? "Cancelled" : r.status_text || `${fmtNum(r.delay_mins)}m late`)],
                ]}
              />
            </div>

            <aside className="analytics-side">
              <section className="analytics-panel">
                <PanelHeader title="Story Lines" meta={cacheMeta?.stale ? "Cached, stale" : "Cached"} />
                <div className="analytics-story-list">
                  {cacheMeta?.missing?.length ? (
                    <div className="analytics-story analytics-story-warning">
                      Missing cached reports: {cacheMeta.missing.join(", ")}
                    </div>
                  ) : null}
                  {storyLines.map((line, idx) => (
                    <div className="analytics-story" key={`${line}-${idx}`}>
                      {line}
                    </div>
                  ))}
                </div>
              </section>

              <section className="analytics-panel">
                <PanelHeader title="Current Highlights" meta={overview?.period} />
                <HighlightRows overview={overview} facts={facts} />
              </section>

              <section className="analytics-panel">
                <PanelHeader title="Worst Flights" meta="Delays and cancellations" />
                <WorstFlights rows={safeList(overview?.rankings?.worst_flights)} />
              </section>
            </aside>
          </section>
        </>
      ) : null}
    </main>
  );
}

function SegmentedControl({ value, onChange, options, label }) {
  return (
    <div className="analytics-control">
      <span>{label}</span>
      <div className="analytics-segmented">
        {options.map((opt) => (
          <button key={opt.value} type="button" className={value === opt.value ? "active" : ""} onClick={() => onChange(opt.value)}>
            {opt.value}
          </button>
        ))}
      </div>
    </div>
  );
}

function SelectControl({ value, onChange, options, label }) {
  return (
    <label className="analytics-control">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function MetricCard({ label, value, sub, delta, suffix = "", higherIsBad = true, compareHidden = false }) {
  const trend = metricTrend(delta, higherIsBad);
  return (
    <article className="analytics-metric">
      <div className="analytics-metric-label">{label}</div>
      <div className="analytics-metric-value">{value}</div>
      <div className={`analytics-metric-delta ${trend}`}>
        <span>{compareHidden ? "Current period" : delta == null ? "No compare" : `${fmtDelta(delta, suffix)} vs prev`}</span>
        {sub ? <span>{compareHidden ? `${sub} rate` : sub}</span> : null}
      </div>
    </article>
  );
}

function PanelHeader({ title, meta }) {
  return (
    <div className="analytics-panel-header">
      <h2>{title}</h2>
      {meta ? <span>{meta}</span> : null}
    </div>
  );
}

function CompareChart({ rows }) {
  const current = rows.filter((r) => r.comparison_bucket === "current");
  const previous = rows.filter((r) => r.comparison_bucket === "previous");
  const max = Math.max(1, ...rows.map((r) => Number(r.delayed_15 || 0) + Number(r.cancelled || 0)));
  const width = 720;
  const height = 260;
  const pad = 36;
  const plotW = width - pad * 2;
  const plotH = height - pad * 2;
  const ordered = (items) => items.slice().sort((a, b) => Number(a.bucket_index) - Number(b.bucket_index));
  const pointObjs = (items) =>
    ordered(items).map((row, idx, arr) => {
      const x = pad + (arr.length <= 1 ? 0 : (idx / (arr.length - 1)) * plotW);
      const value = Number(row.delayed_15 || 0) + Number(row.cancelled || 0);
      const y = pad + plotH - (value / max) * plotH;
      return { x, y, value, row };
    });
  const points = (items) => pointObjs(items).map((point) => `${point.x},${point.y}`).join(" ");
  const currentPoints = pointObjs(current);
  const previousPoints = pointObjs(previous);
  const labels = ordered(current).map((row) => {
    if (row.grain === "hour") return `${String(row.bucket_index).padStart(2, "0")}:00`;
    return `D${Number(row.bucket_index) + 1}`;
  });
  const tickIndexes = labels
    .map((label, idx) => ({ label, idx }))
    .filter((item) => item.idx === 0 || item.idx === labels.length - 1 || item.idx === Math.floor((labels.length - 1) / 2));

  return (
    <div className="analytics-chart-wrap">
      <div className="analytics-chart-meta">
        <span>Metric: delayed 15m+ plus cancellations</span>
        <span>Scale: 0 to {fmtNum(max)}</span>
      </div>
      <svg className="analytics-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Delayed and cancelled flights comparison">
        <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} />
        <line x1={pad} y1={pad} x2={pad} y2={height - pad} />
        <polyline points={points(previous)} className="previous" />
        <polyline points={points(current)} className="current" />
        {previousPoints.map((point, idx) => (
          <circle key={`previous-${idx}`} cx={point.x} cy={point.y} r="3" className="previous-dot" />
        ))}
        {currentPoints.map((point, idx) => (
          <circle key={`current-${idx}`} cx={point.x} cy={point.y} r="3.5" className="current-dot" />
        ))}
      </svg>
      <div className="analytics-chart-axis">
        {tickIndexes.map(({ label, idx }) => (
          <span key={`${label}-${idx}`}>{label}</span>
        ))}
      </div>
      <div className="analytics-chart-legend">
        <span><i className="current" /> Current</span>
        <span><i className="previous" /> Previous</span>
      </div>
      <TrendNumbers rows={rows} />
    </div>
  );
}

function TrendNumbers({ rows }) {
  const current = rows.filter((r) => r.comparison_bucket === "current");
  const previous = rows.filter((r) => r.comparison_bucket === "previous");
  const previousByIndex = new Map(previous.map((row) => [Number(row.bucket_index), row]));
  const orderedCurrent = current.slice().sort((a, b) => Number(a.bucket_index) - Number(b.bucket_index));
  if (!orderedCurrent.length) return null;
  return (
    <div className="analytics-trend-table-wrap">
      <table className="analytics-trend-table">
        <thead>
          <tr>
            <th>Bucket</th>
            <th>Current delay</th>
            <th>Current cancel</th>
            <th>Current total</th>
            <th>Prev delay</th>
            <th>Prev cancel</th>
            <th>Prev total</th>
          </tr>
        </thead>
        <tbody>
          {orderedCurrent.map((row) => {
            const idx = Number(row.bucket_index);
            const prev = previousByIndex.get(idx) || {};
            const currentTotal = Number(row.delayed_15 || 0) + Number(row.cancelled || 0);
            const prevTotal = Number(prev.delayed_15 || 0) + Number(prev.cancelled || 0);
            const label = row.grain === "hour" ? `${String(idx).padStart(2, "0")}:00` : `D${idx + 1}`;
            return (
              <tr key={label}>
                <td>{label}</td>
                <td>{fmtNum(row.delayed_15)}</td>
                <td>{fmtNum(row.cancelled)}</td>
                <td>{fmtNum(currentTotal)}</td>
                <td>{fmtNum(prev.delayed_15)}</td>
                <td>{fmtNum(prev.cancelled)}</td>
                <td>{fmtNum(prevTotal)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function QueueMovement({ rows }) {
  if (!rows.length) return <div className="analytics-empty">No cached queue comparison yet.</div>;
  return (
    <div className="analytics-table-scroll">
      <table className="analytics-table">
        <thead>
          <tr>
            <th>Queue</th>
            <th>Avg wait</th>
            <th>Peak wait</th>
            <th>15m+ samples</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 10).map((row) => (
            <tr key={`${row.terminal}-${row.queue_key}`}>
              <td>{`T${row.terminal} ${row.queue_label}`}</td>
              <td>{fmtDelta(row.deltas?.avg_wait_minutes?.delta, "m")}</td>
              <td>{fmtDelta(row.deltas?.max_wait_minutes?.delta, "m")}</td>
              <td>{fmtDelta(row.deltas?.samples_15_plus?.delta)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RankingTable({ title, rows, columns, empty = "No data available.", limit = 10 }) {
  return (
    <section className="analytics-panel analytics-table-panel">
      <PanelHeader title={title} />
      {rows.length ? (
        <div className="analytics-table-scroll">
          <table className="analytics-table">
            <thead>
              <tr>
                {columns.map(([label]) => (
                  <th key={label}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, limit).map((row, idx) => (
                <tr key={idx}>
                  {columns.map(([label, render]) => (
                    <td key={label}>{render(row)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="analytics-empty">{empty}</div>
      )}
    </section>
  );
}

function HighlightRows({ overview, facts }) {
  const terminal = overview?.highlights?.worst_terminal;
  const airline = overview?.highlights?.worst_airline;
  const aircraft = facts?.facts?.aircraft_types?.[0];
  return (
    <div className="analytics-highlight-list">
      <Highlight label="Worst terminal" value={terminal ? `${terminal.terminal === "Unknown" ? "Unknown" : `T${terminal.terminal}`} - ${fmtPct(terminal.delayed_15_rate_pct)} delayed` : "N/A"} />
      <Highlight label="Worst airline" value={airline ? `${airline.airline_name} - ${fmtPct(airline.delayed_15_rate_pct)} delayed` : "N/A"} />
      <Highlight label="Common aircraft" value={aircraft ? `${aircraft.aircraft_description} (${fmtNum(aircraft.total_flights)})` : "N/A"} />
      <Highlight label="Data source" value={overview?.source_table || "N/A"} />
    </div>
  );
}

function Highlight({ label, value }) {
  return (
    <div className="analytics-highlight">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function WorstFlights({ rows }) {
  if (!rows.length) return <div className="analytics-empty">No delayed or cancelled flights in this view.</div>;
  return (
    <div className="analytics-flight-list">
      {rows.slice(0, 8).map((row) => (
        <div className="analytics-flight-row" key={`${row.flight_identifier}-${row.scheduled_at}`}>
          <div>
            <strong>{row.flight_identifier || row.flight_number}</strong>
            <span>{[row.airline_name, `${row.origin || ""}-${row.destination || ""}`].filter(Boolean).join(" / ")}</span>
          </div>
          <span className={statusClass(row.severity)}>{row.cancelled ? "Cancelled" : `${fmtNum(row.delay_mins)}m`}</span>
        </div>
      ))}
    </div>
  );
}

export default AnalyticsV2;
